import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import {
  ID_MENU_LATERAL,
  ID_MENU_LATERAL_MOBILE,
} from "@components/layout/menuLateral";

const SideNav = ({
  menuItems,
  isMobile,
  mobileOpen,
  onToggle,
  onNavigate,
  activePath,
  appTitle,
  drawerWidth,
  drawerWidthMobile,
  collapsedWidth,
  isCollapsed,
  isHidden = false,
}) => {
  const theme = useTheme();
  // O cabeçalho do menu é a continuação da TopBar: mesmo fundo (o da AppBar do
  // tema) e texto legível sobre ele. `primary.contrastText` só serve no claro; no
  // escuro o MUI usa `text.primary` na AppBar e o contrastText (quase preto)
  // ficaria em ~3:1 sobre o verde.
  const fundoCabecalho =
    theme.components?.MuiAppBar?.styleOverrides?.root?.background ??
    `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 120%)`;
  const corCabecalho =
    theme.palette.mode === "dark"
      ? theme.palette.text.primary
      : theme.palette.primary.contrastText;
  // Mesma sombra da AppBar do tema (as duas faixas formam uma barra só).
  const sombraCabecalho =
    theme.components?.MuiAppBar?.styleOverrides?.root?.boxShadow ??
    `0 10px 24px ${alpha(theme.palette.common.black, 0.18)}`;
  const effectiveWidth = isCollapsed ? collapsedWidth : drawerWidth;
  const appTitleShort = appTitle
    ? appTitle
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "AG";

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 2, md: 3 },
          py: 0,
          minHeight: { xs: 64, md: 72 },
          backgroundImage: fundoCabecalho,
          color: corCabecalho,
          borderBottom: `1px solid ${alpha(
            theme.palette.primary.light,
            0.25,
          )}`,
          boxShadow: sombraCabecalho,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: isCollapsed && !isMobile ? "0.18em" : "0.12em",
            textTransform: "uppercase",
            fontSize: isCollapsed && !isMobile ? "0.95rem" : "1rem",
            textAlign: isCollapsed && !isMobile ? "center" : "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {isCollapsed && !isMobile ? appTitleShort : appTitle}
        </Typography>
        {isMobile && (
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label="Fechar menu de navegacao"
            sx={{ color: corCabecalho }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <List sx={{ pt: 1.5, px: 0.5 }}>
          {menuItems.map((item) => {
            const isActive =
              activePath === item.path ||
              activePath.startsWith(`${item.path}/`);
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => onNavigate(item.path)}
                  selected={isActive}
                  aria-label={item.text}
                  aria-current={isActive ? "page" : undefined}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    py: { xs: 1.25, md: 1 },
                    justifyContent:
                      isCollapsed && !isMobile ? "center" : "flex-start",
                    position: "relative",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0,
                    )}`,
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                      "& .MuiListItemIcon-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      borderColor: alpha(theme.palette.primary.main, 0.14),
                      transform: "translateX(2px)",
                      "& .MuiListItemIcon-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${alpha(
                        theme.palette.primary.main,
                        0.4,
                      )}`,
                      outlineOffset: 2,
                    },
                  }}
                  title={isCollapsed && !isMobile ? item.text : undefined}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed && !isMobile ? 0 : 40,
                      mr: isCollapsed && !isMobile ? 0 : 1,
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      justifyContent: "center",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {(!isCollapsed || isMobile) && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.9rem", md: "0.95rem" },
                        fontWeight: isActive ? 600 : 400,
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  // Oculto (só no desktop; no celular o estado efetivo nunca é "oculto"): nem o
  // `nav` fica no DOM. Sem isso o wrapper reservaria os 260px de largura e o
  // conteúdo não ocuparia a tela, e sobraria um landmark vazio para o leitor de
  // tela. O botão da TopBar continua lá para mostrar o menu de novo.
  if (isHidden) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Menu principal"
      sx={{ width: { md: effectiveWidth }, flexShrink: { md: 0 } }}
    >
      <Drawer
        id={ID_MENU_LATERAL_MOBILE}
        variant="temporary"
        open={mobileOpen}
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidthMobile,
            transition: "transform 0.3s ease",
          },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        id={ID_MENU_LATERAL}
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: effectiveWidth,
            transition: "width 0.3s ease, box-shadow 0.3s ease",
            boxShadow: `4px 0 24px ${alpha(theme.palette.common.black, 0.08)}`,
            overflowX: "hidden",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default SideNav;
